import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { LoggingConfigService } from './services/LoggingConfigService';
import { AppLogger } from './utils/loggers';
import JobMonitorPage from './features/jobs/JobMonitorPage';
import AppLayout from './layout/AppLayout';
import { useApplyAppearancePreferences, useUserPreferencesSync } from './hooks/useUserPreferences';
import { useSystemPreferences } from './hooks/useSystemPreferences';
import { useAppStore } from './store/appStore';
import WsLogViewer from './features/jobs/WsLogViewer';
import JobTracePage from '@/features/jobs/JobTracePage';
import JobTraceGroupViewer from '@/features/jobs/JobTraceGroupViewer';
import SchemaValidationDashboard from '@/features/jobs/SchemaValidationDashboard';
import ContactsDashboard from '@/features/contacts/routes/ContactsDashboard';
import EngagementsDashboard from '@/features/engagements/routes/EngagementsDashboard';
import BillingDashboard from '@/features/billing/routes/BillingDashboard';
import CalendarPage from '@/features/calendar/routes/CalendarPage';
import { LexicalDemo } from '@/features/calendar/routes/LexicalDemo';
import UserManagement from '@/features/admin/routes/UserManagement';
import FirmSettings from '@/features/admin/routes/FirmSettings';
import EdgeMailManagementPage from '@/features/edgemail-admin/routes/EdgeMailManagementPage';
import { lazy, Suspense } from 'react';
const MailClientPage = lazy(() => import('@/features/edgemail-client/routes/MailClientPage'));
const PopOutViewerPage = lazy(() => import('@/features/edgemail-client/routes/PopOutViewerPage'));
import IntakeDashboard from '@/features/intake/routes/IntakeDashboard';
import MyTasksPage from '@/features/tasks/routes/MyTasksPage';
import TaskDetailRoute from '@/features/tasks/routes/TaskDetailRoute';
import DMSDashboard from '@/features/dms/DMSDashboard';
import { TemplatePreviewPage } from '@/features/dms/routes/TemplatePreviewPage';
// import { WsVersion1DocumentEditorPage } from '@/features/dms/routes/wsVersion1DocumentEditorPage'; // COMMENTED OUT - Legacy unused route
import { ClientFirstDocumentEditorPage } from '@/features/dms/routes/ClientFirstDocumentEditorPage';
import { CollaborationAcceptPage } from '@/features/dms/routes/CollaborationAcceptPage';
import { EdgeDocsEditorPage, LexicalEditorPage } from './features/wordprocessing/pages';
import WordDocumentPage from './features/wordprocessing/pages/WordDocumentPage';
import { SearchProvider } from './contexts/SearchContext';
import { setTokenGetter } from './api/axios';
import ParagraphInspector from '@/features/dms/EdgeDocs/EdgeDocsCore/devtools/ParagraphInspector';
import Sprint2LayoutRunner from '@/features/dms/EdgeDocs/EdgeDocsCore/devtools/Sprint2LayoutRunner';
import PageLayoutViewer from '@/features/dms/EdgeDocs/EdgeDocsCore/devtools/PageLayoutViewer';
import Sprint3PlanHashRunner from '@/features/dms/EdgeDocs/EdgeDocsCore/devtools/Sprint3PlanHashRunner';
import EdgeDocsTestLab from '@/features/dms/EdgeDocs/EdgeDocsCore/devtools/EdgeDocsTestLab';
import { EdgedocsCanvasViewer as AstOnlyTypesetterPlayground } from '@/features/dms/components/edgedocs/components/EdgedocsCanvasViewer';
import PaginationTestPage from './features/wordprocessing/pages/PaginationTestPage';
import { LogViewerPage } from './features/logging';
import TaskResponsePage from './pages/TaskResponsePage';
import PublicFormPage from './features/intake/components/PublicFormPage';
import ChatTestRunnerPage from './pages/ChatTestRunnerPage';
const LegacyChatLayout = lazy(() => import('@/features/chat/LegacyChatLayout'));
const LexRuntimePage = lazy(() => import('@/features/lexRuntime/components/LexRuntimePage'));
import FewShotReviewDashboard from '@/features/admin/components/system-tools/few-shot-review/FewShotReviewDashboard';
import CommunicationsDashboard from '@/features/communications/routes/CommunicationsDashboard';

// Test component for LexicalDocumentEditor
import { EdgeDocs as EdgeDocsStandalone } from './features/edgedocs/EdgeDocs';
import { useNavigate } from 'react-router-dom';

function LexicalTestPage(): React.JSX.Element {
  const navigate = useNavigate();
  
  return (
    <div className="h-screen w-screen bg-gray-100">
      <EdgeDocsStandalone
        documentId="test-document"
        onClose={() => {
          AppLogger.component.running('EdgeDocs', { action: 'closed', navigatingTo: '/' });
          navigate('/');
        }}
      />
    </div>
  );
}

function App() {
  AppLogger.component.running('APP COMPONENT', { message: 'BASIC REACT TEST' });
  
  const { keycloak, initialized } = useKeycloak();
  useUserPreferencesSync();
  useApplyAppearancePreferences();
  useSystemPreferences(); // Load and apply subscriber-wide system preferences

  const {
    setCurrentFirmId,
    setCurrentUserId,
    setIsAuthenticated,
  } = useAppStore();

  // Register the Keycloak token getter with Axios for multi-tenant authentication
  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      setTokenGetter(() => keycloak.token || null);
      AppLogger.auth.tokenRegistered(keycloak.realm || 'unknown');
    }
  }, [initialized, keycloak.authenticated, keycloak.token, keycloak.realm]);

  // Initialize LoggingConfigService early so log filtering works immediately
  useEffect(() => {
    if (initialized && keycloak.authenticated && keycloak.token) {
      // Store token for LoggingConfigService to use
      localStorage.setItem('webelieve_access_token', keycloak.token);
      
      // Initialize logging config with current realm
      LoggingConfigService.initialize(keycloak.realm || null)
        .then(() => {
          AppLogger.auth.loggingConfigInit(keycloak.realm || 'unknown');
        })
        .catch((err) => {
          AppLogger.auth.keycloakEvent('loggingConfigFailed', err, { realm: keycloak.realm });
        });
    }
  }, [initialized, keycloak.authenticated, keycloak.token, keycloak.realm]);

  useEffect(() => {
    if (initialized) {
      const isAuthenticated = keycloak.authenticated ?? false;
      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated && keycloak.realm && keycloak.subject) {
        setCurrentFirmId(keycloak.realm);
        setCurrentUserId(keycloak.subject);
      } else {
        setCurrentFirmId(null);
        setCurrentUserId(null);
      }
    }
  }, [
    initialized,
    keycloak.authenticated,
    keycloak.realm,
    keycloak.subject,
    setIsAuthenticated,
    setCurrentFirmId,
    setCurrentUserId,
  ]);

  if (!initialized) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 flex items-center justify-center transition-colors duration-300 ease-in-out">
        <p className="text-xl">Verifying subscriber access & initializing application...</p>
      </div>
    );
  }

  if (!keycloak.authenticated) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 flex items-center justify-center transition-colors duration-300 ease-in-out">
        <div className="text-center">
          <p className="text-xl mb-4">Please authenticate to access WeBelieve</p>
          <button
            onClick={() => keycloak.login()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Login to WeBelieve
          </button>
        </div>
      </div>
    );
  }

  // ✅ Authenticated layout with nested routes
  return (
    <SearchProvider>
      <Routes>
        <Route path="/" element={<AppLayout />}>

          {/* Default route → redirects to Chat */}
          <Route index element={<Navigate to="/chat" replace />} />

          <Route path="chat" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><p>Loading AI…</p></div>}><LexRuntimePage /></Suspense>} />
          <Route path="chat/legacy" element={<Suspense fallback={<div className="flex items-center justify-center h-full"><p>Loading...</p></div>}><LegacyChatLayout /></Suspense>} />
          <Route path="lex-runtime" element={<Navigate to="/chat" replace />} />
          <Route path="projects" element={<Navigate to="/chat" replace />} />
          <Route path="projects/:projectId" element={<Navigate to="/chat" replace />} />
          <Route path="agents" element={<Navigate to="/chat" replace />} />

          {/* 🔹 Dev Tool: Chat Test Runner (admin/system-admin) */}
          <Route path="chat_test_runner" element={<ChatTestRunnerPage />} />

          {/* 🔹 Client Intake Dashboard */}
          <Route path="intake/*" element={<IntakeDashboard />} />
         
          {/* 🔹 Contacts Management */}
          <Route path="contacts/*" element={<ContactsDashboard />} />

          {/* 🔹 Engagements Management */}
          <Route path="engagements/*" element={<EngagementsDashboard />} />
          <Route path="matters/*" element={<EngagementsDashboard />} />

          {/* 🔹 Billing */}
          <Route path="billing/*" element={<BillingDashboard />} />

          {/* 🔹 Document Editor - Client-First Implementation */}
          <Route path="documents/:documentId/editor" element={<EdgeDocsEditorPage />} />

          {/* 🔹 Document Editor - Redirect legacy WebSocket route */}
          <Route path="documents/:documentId/editor-ws" element={<Navigate to="../editor" replace />} />

          {/* 🔹 Document Editor - Legacy Routes (fallback) */}
          <Route path="documents/:documentId/editor-legacy" element={<ClientFirstDocumentEditorPage />} />
          {/* <Route path="documents/:documentId/editor-ws-legacy" element={<WsVersion1DocumentEditorPage />} /> */} {/* COMMENTED OUT - Legacy unused route */}

          {/* 🔹 Document Editor - Standalone Route (legacy) - DISABLED to prevent conflicts with ClientFirst */}
          {/* <Route path="documents/edge-docs/:documentId" element={<DMSOrchestrator />} /> */}

          {/* 🔹 Word Document Editor - MS Word Integration Phase 1 */}
          <Route path="documents/:documentId/word-editor" element={<WordDocumentPage />} />

          {/* 🔹 NEW WORD PROCESSING EDITORS - Orchestrator Architecture */}
          <Route path="/dms/editor/:docId" element={<EdgeDocsEditorPage />} />
          <Route path="/dms/editor-lexical/:docId" element={<LexicalEditorPage />} />

          {/* 🔹 Document Management */}
          <Route path="documents/*" element={<DMSDashboard />} />
          {/* 🔹 Template Preview (opens after sessionStorage seed) */}
          <Route path="dms/template-preview" element={<TemplatePreviewPage />} />
          
          {/* 🔹 Collaboration Invitation Acceptance */}
          <Route path="collaboration/accept" element={<CollaborationAcceptPage />} />

          {/* 🔹 Task Management */}
          <Route path="tasks/:taskId" element={<TaskDetailRoute />} />
          <Route path="tasks" element={<MyTasksPage />} />

          {/* 🔹 Calendar Management */}
          <Route path="calendar" element={<CalendarPage />} />
          
          {/* 🔹 Lexical Demo - Deep Legal Entity Integration */}
          <Route path="lexical-demo" element={<LexicalDemo />} />

          {/* 🔹 Admin Tools */}
          <Route path="admin/user-management" element={<UserManagement />} />
          <Route path="admin/settings" element={<FirmSettings />} />
          <Route path="admin/edgemail/*" element={<EdgeMailManagementPage />} />

          {/* WeBeMail Web Client (code-split) */}
          <Route path="email" element={
            <Suspense fallback={<div className="flex items-center justify-center h-screen bg-gray-950 text-gray-300">Loading WeBeMail...</div>}>
              <MailClientPage />
            </Suspense>
          } />

          {/* Pop-out viewer moved outside AppLayout — see below */}

          {/* 🛠️ Devtools: EdgeDocs Paragraph Inspector */}
          <Route path="dev/edgedocs/paragraph" element={<ParagraphInspector />} />
          {/* 🛠️ Devtools: EdgeDocs Sprint 2 Layout Runner */}
          <Route path="dev/edgedocs/tests/layout" element={<Sprint2LayoutRunner />} />
          {/* 🛠️ Devtools: EdgeDocs Page Layout Viewer */}
          <Route path="dev/edgedocs/pages" element={<PageLayoutViewer />} />
          {/* 🛠️ Devtools: EdgeDocs Sprint 3 PlanHash Runner */}
          <Route path="dev/edgedocs/tests/planhash" element={<Sprint3PlanHashRunner />} />
          {/* 🛠️ Devtools: EdgeDocs Test Lab */}
          <Route path="dev/edgedocs/test-lab" element={<EdgeDocsTestLab />} />
          {/* 🛠️ Devtools: EdgeDocs Pagination Test (NEW - Phase 3) */}
          <Route path="dev/edgedocs/pagination-test" element={<PaginationTestPage />} />

          {/* 🔹 AST-only Typesetter Playground (no browser layout) */}
          <Route path="edgedocs/ast-playground" element={<AstOnlyTypesetterPlayground />} />

          {/* 🔹 Observability: Log Viewer (admin/developer only) */}
          <Route path="logs" element={<LogViewerPage />} />

          {/* 🔹 System Monitor Main View */}
          <Route path="system-monitor" element={<JobMonitorPage />} />
          {/* 🔹 System Monitor Main View */}
          
          {/* 🔹 Subroutes for System Monitor */}
          <Route path="system-monitor/ws-log" element={<WsLogViewer />} />
          <Route path="system-monitor/schema-validation" element={<SchemaValidationDashboard />} />
          <Route path="system-monitor/job-trace/:jobId" element={<JobTracePage />} />
          <Route path="system-monitor/trace-group/:traceGroupId" element={<JobTraceGroupViewer />} />
          <Route path="system-monitor/few-shot-review" element={<FewShotReviewDashboard />} />
          <Route path="system-tools/communications" element={<CommunicationsDashboard />} />

          {/* 🔹 Redirects for legacy/admin paths */}
          <Route path="admin/*" element={<Navigate to="/admin/user-management" replace />} />
          <Route path="admin" element={<Navigate to="/admin/user-management" replace />} />
        </Route>

        {/* WeBeMail Pop-Out Viewer — standalone, no sidebar/header */}
        <Route path="/email/view/:mailboxId/:messageId" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen bg-[#161e2e] text-gray-300">Loading message...</div>}>
            <PopOutViewerPage />
          </Suspense>
        } />

        {/* 🧪 TEST ROUTE - Isolated Lexical Editor */}
        <Route path="/test-lexical" element={<LexicalTestPage />} />

        {/* 🔹 Public: Task Assignment Response (no auth required) */}
        <Route path="/task-response/:token" element={<TaskResponsePage />} />

        {/* 🔹 Public: Client Intake Form (no auth required) */}
        <Route path="/form/:formId" element={<PublicFormPage />} />

        {/* 🔹 Fallback: Unknown routes redirect to chat */}
        <Route path="*" element={<Navigate to="/chat" replace />} />
      </Routes>
    </SearchProvider>
  );
}

export default App;
